import React from 'react';
import last from 'lodash.last';
import isEmpty from 'lodash.isempty';
import head from 'lodash.head';
import { getNavigationUrl } from '../../../utils/shared/urlUtils';
import { MEASURE } from '../../../constants/shared/MeasuresConstants';
import { GRID, PERIOD, getToyTitleByKey } from '../../../constants/shared/CommonConstants';
import {
    SkillHeaderCell,
    GradeCompositeCell,
    GradeNameCell,
    GradeScoreCell,
    StudentNameCell,
} from '../../cell/shared/Cells';
import {
    CELL_TYPE,
    INDEX,
    CELL_MEASUREMENTS,
    AMP_NAV_HEIGHT,
} from '../../../constants/shared/CellConstants';
import { applyRightBorder } from '../../../utils/shared/cellUtils';
import { getGradeTitle } from '../../../constants/shared/GradesConstants';
import { getStudentResultByMeasureYearGradePeriod } from '../../../utils/shared/filterUtils';

const { SUMMARY, SUMMER } = PERIOD;
const { OVERALL } = MEASURE;
const { INPUT, BENCHMARK_GATING_MEASURE } = CELL_TYPE;

const filterColumnsByColumnsGroup = (columns, columnsGroup) =>
    columnsGroup.reduce((filteredColumns, columnInGroup) => {
        if (columns.includes(columnInGroup)) {
            return [...filteredColumns, columnInGroup];
        }
        return [...filteredColumns];
    }, []);

/**
 * Gets list of columns which are last in every provided group
 */
const getLastColumnsInGroup = (columns, columnsGroups) =>
    columnsGroups.map(columnsGroup => last(filterColumnsByColumnsGroup(columns, columnsGroup)));

export const columnsWithBorder = (columns, columnsGroups) =>
    getLastColumnsInGroup(
        columns.map(column => column.key),
        columnsGroups,
    );

const mapSkillNamesToRowObject = (selectedPeriod, tabelHeadersCells, summerPeriodLabel) => {
    const skillNamesRowObject = {
        studentName: (
            <SkillHeaderCell
                label={getToyTitleByKey(selectedPeriod, summerPeriodLabel)}
                containerClassNames={`header-toy-cell-container ${
                    selectedPeriod === SUMMARY ? ' header-skill-cell-right-border' : ''
                } `}
                classNames="header-toy-name-cell"
            />
        ),
    };
    return { ...skillNamesRowObject, ...tabelHeadersCells };
};

const mapGradeToRowObject = (
    grade,
    period,
    columns,
    columnsGroup,
    isFirstRow,
    summerPeriodLabel,
    isResetButtonAvailableInClass,
) => {
    const gradeRowObject = {
        name: <GradeNameCell name={getGradeTitle(grade)} showSortingIcon isFirstRow={isFirstRow} />,
    };
    columns.forEach(column => {
        const { key: measure, goalSection } = column;
        const periodToShow = column.period === PERIOD.SUMMER ? summerPeriodLabel : column.period;
        const name = period === SUMMARY ? periodToShow : column.name;
        const rowKey = period === SUMMARY ? measure + column.period : measure;
        const isRightBorderTurnedOn = period !== SUMMARY;

        gradeRowObject[rowKey] =
            measure === OVERALL && period !== SUMMARY ? (
                <GradeCompositeCell
                    name={name}
                    goalSection={goalSection}
                    isFirstRow={isFirstRow}
                    isResetButtonAvailableInClass={isResetButtonAvailableInClass}
                />
            ) : (
                <GradeScoreCell
                    name={name}
                    showSortingIcon
                    goalSection={goalSection}
                    isFirstRow={isFirstRow}
                    optionalCellData={{
                        classNames: applyRightBorder(
                            isRightBorderTurnedOn &&
                                columnsWithBorder(columns, columnsGroup).includes(measure),
                        ),
                    }}
                />
            );
    });
    return gradeRowObject;
};

const mapStudentToRowObject = ({
    studentInfo,
    grade,
    columns,
    selectedPeriod,
    location,
    openAdditionalPanel,
    openProbeDetails,
    isDyslexiaRiskDisplayed,
    isStudentHasUnofficialResults,
    isPercentileShown,
    columnsGroup,
    LinkElement,
    baseUrl,
    isDemoModeOn,
    CommonCellBuilder,
    additionalParams,
    isPercentileToggleOn,
    isResetButtonAvailableInClass,
    showSecondAttemptBulkInvalidationModal,
}) => {
    const studentRowObject = {};
    const { isTransitionalKindergarten, id } = studentInfo;
    studentRowObject.name = (
        <StudentNameCell
            studentInfo={studentInfo}
            isDyslexiaRiskDisplayed={isDyslexiaRiskDisplayed}
            isTransitionalKindergarten={isTransitionalKindergarten}
            isUnofficial={isStudentHasUnofficialResults}
            LinkElement={LinkElement}
            navigationUrl={getNavigationUrl(location, baseUrl, {
                studentId: id,
                ...additionalParams,
            })}
        />
    );
    columns.forEach(column => {
        const {
            key: measure,
            sortIndex,
            isInputCell,
            isCellDisabled,
            isResetButtonAvailable,
        } = column;
        const isSummarySelectedPeriod = selectedPeriod === SUMMARY;
        const rowKey = isSummarySelectedPeriod ? measure + column.period : measure;
        const period = isSummarySelectedPeriod ? column.period : selectedPeriod;
        const isRightBorderTurnedOn = !isSummarySelectedPeriod;

        studentRowObject[rowKey] = (
            <CommonCellBuilder
                cellType={isInputCell ? INPUT : CELL_TYPE.BENCHMARK}
                statusForMeasureYearPeriod={column.status}
                measure={measure}
                isDemoModeOn={isDemoModeOn}
                period={period}
                grade={grade}
                openPanel={isInputCell ? openAdditionalPanel : openProbeDetails}
                externalId={id}
                location={location}
                gatingMeasureCellType={BENCHMARK_GATING_MEASURE}
                optionalCellData={{
                    studentInfo,
                    isPercentileShown: period !== SUMMER && isPercentileShown,
                    classNames: `${applyRightBorder(
                        isRightBorderTurnedOn &&
                            columnsWithBorder(columns, columnsGroup).includes(measure),
                    )} ${measure.toLowerCase()}-cell`,
                    percentileClassName: isPercentileToggleOn ? 'percentile' : '',
                }}
                isCellDisabled={isCellDisabled}
                sortIndex={sortIndex}
                resetCellData={{
                    isResetButtonAvailable,
                    isResetButtonAvailableInClass,
                    showSecondAttemptBulkInvalidationModal,
                }}
            />
        );
    });
    return studentRowObject;
};

export const gridProps = {
    areSubHeadersSticky: true,
    stickyColumnList: [INDEX.FIRST_ARRAY_INDEX],
    defaultCellMeasurements: CELL_MEASUREMENTS.DEFAULT_BENCHMARK,
    scrollMode: GRID.SCROLL_MODE.WINDOW,
    // we don't need Amplify Grid default styles, need to discuss this with SWAT
    gridHeaderStyles: {},
    subHeaderStyles: {
        height: '80px !important',
    },
    dataCellStyles: {},
    topOffset: AMP_NAV_HEIGHT,
};

export const getGridProps = (selectedPeriod, toyPeriods, summaryHeaderColumns = []) => {
    // TO DO look at moot data
    const gridPropsData = {
        ...gridProps,
        colSpans: [],
    };

    // building SUMMARY header cells with colspans
    if (selectedPeriod === PERIOD.SUMMARY) {
        summaryHeaderColumns.forEach((measure, columnIndex) =>
            gridPropsData.colSpans.push({
                row: 0,
                columnStartIndex: toyPeriods.length * columnIndex + 1,
                columnStopIndex: toyPeriods.length * columnIndex + toyPeriods.length,
                child: (
                    <SkillHeaderCell
                        label={measure.title}
                        containerClassNames="header-toy-cell-container header-skill-cell-right-border"
                        classNames="header-summary-measure-name-cell header-skill-cell-font"
                    />
                ),
            }),
        );
    }

    return gridPropsData;
};

export const getRowsToRender = ({
    selectedPeriod,
    location,
    openAdditionalPanel,
    openProbeDetails,
    benchmarkData,
    columnsGroup,
    LinkElement,
    baseUrl,
    isDemoModeOn,
    CommonCellBuilder,
    additionalParams,
    isPercentileShown,
    tabelHeadersCells,
    isPercentileToggleOn,
    summerPeriodLabel,
    isResetButtonAvailableInClass = false,
    showSecondAttemptBulkInvalidationModal = () => {},
}) => {
    const rowsToRender = [];
    const { rows } = benchmarkData;
    // push skills header
    rowsToRender.push({
        rowObject: mapSkillNamesToRowObject(selectedPeriod, tabelHeadersCells, summerPeriodLabel),
        rowProperties: { isGridHeader: true, isSubheader: true },
    });
    // iterate over grades and push grade subheader or student row
    rows.forEach((row, index) => {
        const isFirstRow = index === 0;
        const { students, grade } = row;
        if (!isEmpty(students)) {
            rowsToRender.push({
                rowObject: mapGradeToRowObject(
                    grade,
                    selectedPeriod,
                    head(students).columns,
                    columnsGroup,
                    isFirstRow,
                    summerPeriodLabel,
                    isResetButtonAvailableInClass,
                ),
                rowProperties: { isSubheader: true },
            });
            students.forEach(student => {
                const {
                    columns,
                    studentInfo,
                    isDyslexiaRiskDisplayed,
                    isStudentHasUnofficialResults,
                } = student;

                rowsToRender.push({
                    rowObject: mapStudentToRowObject({
                        studentInfo,
                        grade,
                        columns,
                        selectedPeriod,
                        location,
                        openAdditionalPanel,
                        openProbeDetails,
                        isDyslexiaRiskDisplayed,
                        isStudentHasUnofficialResults,
                        isPercentileShown,
                        isPercentileToggleOn,
                        columnsGroup,
                        LinkElement,
                        baseUrl,
                        isDemoModeOn,
                        CommonCellBuilder,
                        additionalParams,
                        isResetButtonAvailableInClass,
                        showSecondAttemptBulkInvalidationModal,
                    }),
                });
            });
        }
    });
    return rowsToRender;
};

export const getClassSummaryColumns = (availableMeasures, selectedPeriod, statuses, year) =>
    availableMeasures.map(measure => {
        const status = getStudentResultByMeasureYearGradePeriod(
            statuses,
            measure,
            year,
            null,
            selectedPeriod,
        );

        return {
            measure,
            status,
        };
    });
